import { db } from '../firebase-config';
import { doc, getDoc, setDoc, collection, getDocs, query, orderBy } from 'firebase/firestore';
import { industryTemplates, defaultAgents } from '../data/industryTemplates';

// Get agent configuration by ID
export async function getAgentConfig(agentId) {
  try {
    // Check if it's a default agent
    if (defaultAgents[agentId]) {
      return defaultAgents[agentId];
    }
    
    // Try to fetch from Firebase
    const docRef = doc(db, 'agents', agentId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { id: agentId, ...docSnap.data() };
    }
    
    // Return null if agent not found
    return null;
  } catch (error) {
    console.error('Error fetching agent config:', error);
    return null;
  }
}

// Get all agents
export async function getAllAgents() {
  try {
    const agents = [];
    
    // Add default agents
    Object.values(defaultAgents).forEach(agent => {
      agents.push(agent);
    });
    
    // Fetch custom agents from Firebase
    const q = query(collection(db, 'agents'), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    
    querySnapshot.forEach((doc) => {
      agents.push({ id: doc.id, ...doc.data() });
    });
    
    return agents;
  } catch (error) {
    console.error('Error fetching all agents:', error);
    return Object.values(defaultAgents); // Return defaults on error
  }
}

// Save agent configuration
export async function saveAgentConfig(agentData) {
  try {
    const docRef = doc(db, 'agents', agentData.id);
    await setDoc(docRef, {
      ...agentData,
      updatedAt: new Date().toISOString()
    });
    return true;
  } catch (error) {
    console.error('Error saving agent config:', error);
    return false;
  }
}

// Get complete agent configuration with industry template merged
export function getCompleteAgentConfig(agent) {
  if (!agent) return null;
  
  const industryTemplate = industryTemplates[agent.industry];
  if (!industryTemplate) return agent;
  
  return {
    ...agent,
    branding: agent.customBranding || industryTemplate.branding,
    questions: agent.customQuestions || industryTemplate.questions,
    messaging: {
      ...industryTemplate.messaging,
      ...agent.customMessaging
    },
    industryTemplate
  };
}

// Parse URL parameters
export function parseUrlParams() {
  const urlParams = new URLSearchParams(window.location.search);
  return {
    agentId: urlParams.get('agent') || null, // No default agent
    isAdmin: urlParams.get('admin') === 'true',
    preview: urlParams.get('preview') === 'true'
  };
}

// Generate agent URL
export function generateAgentUrl(agentId, baseUrl = window.location.origin) {
  if (agentId === 'default') {
    return baseUrl;
  }
  return `${baseUrl}?agent=${agentId}`;
}

// Generate admin URL
export function generateAdminUrl(baseUrl = window.location.origin) {
  return `${baseUrl}?admin=true`;
}

// Validate agent data
export function validateAgentData(agentData) {
  const errors = [];
  
  if (!agentData.name || agentData.name.trim().length === 0) {
    errors.push('Agent name is required');
  }
  
  if (!agentData.industry || !industryTemplates[agentData.industry]) {
    errors.push('Valid industry is required');
  }
  
  if (!agentData.id || agentData.id.trim().length === 0) {
    errors.push('Agent ID is required');
  }
  
  // Validate ID format (alphanumeric and hyphens only)
  if (agentData.id && !/^[a-zA-Z0-9-]+$/.test(agentData.id)) {
    errors.push('Agent ID can only contain letters, numbers, and hyphens');
  }
  
  return errors;
}

// Create new agent with defaults
export function createNewAgent(industry = 'Therapist/Mental Health') {
  const template = industryTemplates[industry];
  if (!template) throw new Error('Invalid industry');
  
  return {
    id: '',
    name: '',
    industry,
    photo: '',
    customBranding: null,
    customQuestions: null,
    customMessaging: null,
    active: true,
    createdAt: new Date().toISOString()
  };
}
